import * as React from 'react';

import { useIntl } from 'react-intl';

import { useLogout } from 'src/features/auth/api/useLogout';
import { useGetCurrentUser } from 'src/features/profile/api/useGetCurrentUser';
import ProfileInfoForm from 'src/features/profile/components/ProfileInfoForm';
import ProfileLayout from 'src/features/profile/components/ProfileLayout';
import { detailsMessages, profileMessages } from 'src/features/profile/utils/messages';
import { getCurrentUserId } from 'src/features/profile/utils/getCurrentUserId';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { Button } from 'src/registry/ui/Button';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';

const Details = () => {
    const { formatMessage } = useIntl();
    const userId = getCurrentUserId();
    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(profileMessages.title));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data, isLoading, isError, refetch } = useGetCurrentUser({
        userId,
        config: { enabled: !!userId },
    });

    const { mutate: logout } = useLogout({
        onSuccess: (cleanup) => {
            cleanup();
            window.location.reload();
        },
    });

    return (
        <ProfileLayout>
            <SectionBlock>
                <SectionBlockHeader>
                    <SectionBlockHead>
                        <SectionBlockTitle>{formatMessage(detailsMessages.personalInfoTitle)}</SectionBlockTitle>
                        <SectionBlockSubtitle>
                            {formatMessage(detailsMessages.personalInfoSubtitle)}
                        </SectionBlockSubtitle>
                    </SectionBlockHead>
                </SectionBlockHeader>
                <SectionBlockSeparator />
                <SectionBlockBody className="flex-col">
                    {isLoading && (
                        <div className="flex w-full flex-col gap-4">
                            <Skeleton className="h-12 w-full" />
                            <Skeleton className="h-12 w-full" />
                        </div>
                    )}
                    {isError && <GeneralMessageBlock variant="error" icon={ICON_MAP.CIRCLE_EXCLAMATION} />}
                    {data && <ProfileInfoForm data={data} refetch={refetch} />}
                </SectionBlockBody>
            </SectionBlock>

            <SectionBlock>
                <SectionBlockHeader>
                    <SectionBlockHead>
                        <SectionBlockTitle>{formatMessage(detailsMessages.accountInfoTitle)}</SectionBlockTitle>
                        <SectionBlockSubtitle>
                            {formatMessage(detailsMessages.accountInfoSubtitle)}
                        </SectionBlockSubtitle>
                    </SectionBlockHead>
                </SectionBlockHeader>
                <SectionBlockSeparator />
                <div className="flex justify-end">
                    <Button variant="destructiveLight" onClick={() => logout(undefined)}>
                        {formatMessage(detailsMessages.logout)}
                    </Button>
                </div>
            </SectionBlock>
        </ProfileLayout>
    );
};

export default Details;
